// src/lib/campaigns/useCampaigns.tsx
//
// Reactive store for partner-marketing campaigns.
//
// React Context + useReducer + localStorage under 'bench-campaigns-v1'.
// Schema-guarded loader (drops corrupt payloads silently).
//
// Surfaces:
//   useCampaigns() -> { campaigns, getById, create, advance, remove, reset }
//   templates() -> array of CampaignStep defaults per play key
//
// The agent B (Marketing Plan) consultation path is modelled here:
// pick a play, a tier, a fire time, draft steps. HITL happens when
// `advance(c, 'scheduled', ...)` runs — the marketing lead must click.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type ReactNode,
} from "react";
import { DEMO_CUSTOMERS } from "@/lib/demoData";
import {
  CAMPAIGN_PLAY_LABEL,
  type Campaign,
  type CampaignPlayKey,
  type CampaignStatus,
  type CampaignStep,
} from "./types";

const STORAGE_KEY = "bench-campaigns-v1";

// ---------- audience math ----------

export function countAudience(
  tier: Campaign["audienceTier"],
  customers = DEMO_CUSTOMERS,
): number {
  if (tier === "all") return customers.length;
  return customers.filter((c) => c.pricingTier === tier).length;
}

// ---------- step templates per play ----------

export function buildStepsForPlay(playKey: CampaignPlayKey): CampaignStep[] {
  switch (playKey) {
    case "play-1-tier-drip":
      return [
        {
          kind: "email",
          dayOffset: 0,
          subject: "Your tier-priced drops for this month",
          body:
            "Open with the tier-specific framing: VIP get first-look; " +
            "Preferred get back-in-stock alerts; Standard get Top 10.",
        },
      ];
    case "play-2-cart-abandon":
      return [
        {
          kind: "email",
          dayOffset: 0,
          subject: "Still thinking it over?",
          body:
            "Regional rep follows up within 24h with availability and " +
            "(where applicable) tier pricing.",
        },
      ];
    case "play-3-back-in-stock":
      return [
        {
          kind: "email",
          dayOffset: 0,
          subject: "It's back — and reserved for you",
          body:
            "Holding-cost frame the subject line; tie tier exclusivity in the body.",
        },
      ];
    case "play-4-brand-story":
      return [
        {
          kind: "blog-draft",
          dayOffset: 0,
          subject: "Long-form install case study (brand)",
          body:
            "Article draft + 90-second social cut. Same caption, two channels.",
        },
      ];
    case "play-5-co-marketing":
      return [
        {
          kind: "email",
          dayOffset: 0,
          subject: "BENCHMARK x <partner shop name> — joint offer",
          body:
            "BENCHMARK ships product + pricing; partner shop adds local " +
            "install / measurement. Kpi: open >=35%, click-to-quote >=8%.",
        },
      ];
    case "play-6-event-loop":
      return [
        {
          kind: "blog-draft",
          dayOffset: 0,
          subject: "Booth recap + 30-second reel",
          body:
            "Long cut -> YouTube; vertical cut -> Telegram reels; stills " +
            "+ alt-text -> partner-shop site.",
        },
      ];
    case "play-7-comparison-seo":
      return [
        {
          kind: "blog-draft",
          dayOffset: 0,
          subject: "Comparison: <sku a> vs <sku b>",
          body:
            "Neutral voice, no vendor bias. Long-tail SEO target; link " +
            "to /catalog product pages.",
        },
      ];
    case "play-8-tier-gated-drop":
      return [
        {
          kind: "email",
          dayOffset: 0,
          subject: "New <brand> SKU — first-look for VIP / Preferred",
          body:
            "Standard tier gets the public-catalog headline list price; " +
            "VIP + Preferred see their tier discount + 14-day exclusivity window.",
        },
      ];
    case "play-9-vip-at-risk":
      return [
        {
          kind: "call-task",
          dayOffset: 0,
          subject: "VIP at risk — review + rep follow-up",
          body:
            "Brief: top 5 prior SKUs + brand watch list. Rep has 5 business days to act.",
        },
      ];
    case "play-10-install-referral":
      return [
        {
          kind: "email",
          dayOffset: 0,
          subject: "Refer a partner shop, lock Preferred for 6 months",
          body:
            "Referred shop starts Standard for 90 days; auto-upgrades to " +
            "Preferred if first order > EUR 5k.",
        },
      ];
    case "play-11-post-install-drip":
      return [
        {
          kind: "email",
          dayOffset: 0,
          subject: "Did your install land?",
          body: "Day 0 — link to room-layout checklist PDF.",
        },
        {
          kind: "email",
          dayOffset: 3,
          subject: "Calibration video from a local integrator",
          body: "Day 3 — calibration walk-through.",
        },
        {
          kind: "email",
          dayOffset: 7,
          subject: "How did it land? Reply or share a photo",
          body:
            "Day 7 — review request + offer to feature as a case study.",
        },
      ];
    case "play-12-qbr-deck":
      return [
        {
          kind: "blog-draft",
          dayOffset: 0,
          subject: "QBR deck for <customer name>",
          body:
            "One-page PDF: YTD spend vs prior YTD, untried SKUs, tier " +
            "upsell signal. Emailed by the regional rep.",
        },
      ];
  }
}

// ---------- storage ----------

function loadFromStorage(): Campaign[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      Array.isArray(parsed) &&
      parsed.every(
        (c) =>
          typeof c === "object" &&
          c &&
          "id" in c &&
          "playKey" in c &&
          "timeline" in c,
      )
    ) {
      return parsed as Campaign[];
    }
    return null;
  } catch {
    return null;
  }
}

function saveToStorage(rows: Campaign[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
  } catch {
    // ignore quota / private-mode
  }
}

// ---------- minting ----------

function nextCampaignId(rows: Campaign[]): string {
  let max = 0;
  for (const r of rows) {
    const m = /^CMP-(\d+)$/.exec(r.id);
    if (m && m[1]) {
      const n = Number(m[1]);
      if (Number.isFinite(n) && n > max) max = n;
    }
  }
  return `CMP-${String(max + 1).padStart(4, "0")}`;
}

// ---------- seed ----------

function isoIn(days: number, hour = 9): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + days);
  d.setUTCHours(hour, 0, 0, 0);
  return d.toISOString();
}

function seed(): Campaign[] {
  const now = new Date().toISOString();
  const tieredDrip: Campaign = {
    id: "CMP-0001",
    name: "VIP July drops — first-look invite",
    playKey: "play-1-tier-drip",
    audienceTier: "vip",
    status: "scheduled",
    fireAt: isoIn(3),
    steps: buildStepsForPlay("play-1-tier-drip"),
    createdAt: now,
    timeline: [
      {
        ts: now,
        status: "draft",
        actor: "system",
        note: "Drafted from emarketing Play 1 against the VIP tier.",
      },
      {
        ts: now,
        status: "scheduled",
        actor: "human",
        note: "Marketing lead approved. Held for HITL approval on fire.",
      },
    ],
  };
  const cartAbandon: Campaign = {
    id: "CMP-0002",
    name: "Cart nudge — Studio Norden (7d abandoned)",
    playKey: "play-2-cart-abandon",
    audienceTier: "all",
    status: "draft",
    fireAt: isoIn(1, 14),
    steps: buildStepsForPlay("play-2-cart-abandon"),
    createdAt: now,
    timeline: [
      {
        ts: now,
        status: "draft",
        actor: "agent",
        note: "Detected 7-day abandoned cart from portal activity.",
      },
    ],
  };
  const postInstall: Campaign = {
    id: "CMP-0003",
    name: "Post-install drip — SoundHouse (RollaTv 85 Pro)",
    playKey: "play-11-post-install-drip",
    audienceTier: "standard",
    status: "sending",
    fireAt: isoIn(-2),
    steps: buildStepsForPlay("play-11-post-install-drip"),
    createdAt: now,
    timeline: [
      {
        ts: now,
        status: "draft",
        actor: "system",
        note: "Triggered by order delivered event (SHP-0002 style).",
      },
      {
        ts: now,
        status: "scheduled",
        actor: "human",
        note: "Approved; in Drip queue.",
      },
      {
        ts: now,
        status: "sending",
        actor: "agent",
        note: "Day 0 email delivered.",
      },
    ],
  };
  return [tieredDrip, cartAbandon, postInstall];
}

// ---------- reducer ----------

export type CampaignAction =
  | { type: "replaceAll"; payload: Campaign[] }
  | { type: "reset" }
  | { type: "create"; payload: Campaign }
  | {
      type: "advance";
      payload: {
        id: string;
        next: CampaignStatus;
        actor: "system" | "agent" | "human";
        note: string;
      };
    }
  | { type: "remove"; payload: { id: string } };

function reducer(rows: Campaign[], action: CampaignAction): Campaign[] {
  switch (action.type) {
    case "replaceAll":
      return action.payload;
    case "reset":
      return seed();
    case "create":
      return [action.payload, ...rows];
    case "advance":
      return rows.map((r) => {
        if (r.id !== action.payload.id) return r;
        return {
          ...r,
          status: action.payload.next,
          timeline: [
            ...r.timeline,
            {
              ts: new Date().toISOString(),
              status: action.payload.next,
              actor: action.payload.actor,
              note: action.payload.note,
            },
          ],
        };
      });
    case "remove":
      return rows.filter((r) => r.id !== action.payload.id);
  }
}

// ---------- context ----------

export interface CampaignsContextValue {
  campaigns: Campaign[];
  getById: (id: string) => Campaign | undefined;
  create: (
    input: Omit<Campaign, "id" | "createdAt" | "timeline" | "status">,
  ) => Campaign;
  advance: (
    id: string,
    next: CampaignStatus,
    actor: "system" | "agent" | "human",
    note: string,
  ) => void;
  remove: (id: string) => void;
  reset: () => void;
  templateStepsForPlay: typeof buildStepsForPlay;
  audienceCount: (tier: Campaign["audienceTier"]) => number;
  playOptions: Array<{
    key: CampaignPlayKey;
    label: string;
    skillNumber: number;
  }>;
}

const CampaignsContext = createContext<CampaignsContextValue | null>(null);

export function CampaignsProvider({ children }: { children: ReactNode }) {
  const [rows, dispatch] = useReducer(
    reducer,
    undefined,
    () => loadFromStorage() ?? seed(),
  );

  useEffect(() => {
    saveToStorage(rows);
  }, [rows]);

  const value = useMemo<CampaignsContextValue>(() => {
    const playOptions = (Object.keys(CAMPAIGN_PLAY_LABEL) as CampaignPlayKey[])
      .map((k) => ({
        key: k,
        label: CAMPAIGN_PLAY_LABEL[k],
        skillNumber: Number(k.match(/play-(\d+)/)?.[1] ?? 0),
      }))
      .sort((a, b) => a.skillNumber - b.skillNumber);

    return {
      campaigns: rows,
      getById: (id) => rows.find((r) => r.id === id),
      create: (input) => {
        const created: Campaign = {
          id: nextCampaignId(rows),
          status: "draft",
          createdAt: new Date().toISOString(),
          timeline: [
            {
              ts: new Date().toISOString(),
              status: "draft",
              actor: "system",
              note: `Drafted from emarketing Play ${String(
                input.playKey.match(/play-(\d+)/)?.[1] ?? "?",
              )}.`,
            },
          ],
          ...input,
        };
        dispatch({ type: "create", payload: created });
        return created;
      },
      advance: (id, next, actor, note) =>
        dispatch({
          type: "advance",
          payload: { id, next, actor, note },
        }),
      remove: (id) => dispatch({ type: "remove", payload: { id } }),
      reset: () => dispatch({ type: "reset" }),
      templateStepsForPlay: buildStepsForPlay,
      audienceCount: countAudience,
      playOptions,
    };
  }, [rows]);

  return (
    <CampaignsContext.Provider value={value}>
      {children}
    </CampaignsContext.Provider>
  );
}

export function useCampaigns(): CampaignsContextValue {
  const ctx = useContext(CampaignsContext);
  if (!ctx) {
    throw new Error(
      "useCampaigns must be used inside a <CampaignsProvider />",
    );
  }
  return ctx;
}

export function _resetCampaignsStorage(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
